import React from 'react';
import { Link } from 'react-router-dom';

export default function Footer() {
    const currentYear = new Date().getFullYear();

    return (
        <footer className="relative bg-gray-950 dark:bg-royal-800 text-white overflow-hidden">
            {/* Decorative top border */}
            <div className="h-px bg-gradient-to-r from-transparent via-gold-500 to-transparent" />

            <div className="container-custom section-padding pb-8">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
                    {/* Brand */}
                    <div className="lg:col-span-1">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-gold-400 to-gold-600 flex items-center justify-center">
                                <span className="font-display text-white font-bold text-sm">UC</span>
                            </div>
                            <div>
                                <h3 className="font-display text-xl font-bold">Eden Valley</h3>
                                <p className="text-[10px] tracking-[0.2em] uppercase text-gold-400 font-sans">Kodaikanal</p>
                            </div>
                        </div>
                        <p className="font-body text-gray-400 leading-relaxed text-sm">
                            A premium homestay nestled in the serene hills of Kookal, Kodaikanal.
                            Where nature meets luxury.
                        </p>
                    </div>

                    {/* Quick Links */}
                    <div>
                        <h4 className="font-display text-lg font-semibold mb-6 text-gold-400">Quick Links</h4>
                        <ul className="space-y-3">
                            {[
                                { to: '/', label: 'Home' },
                                { to: '/rooms', label: 'Rooms' },
                                { to: '/gallery', label: 'Gallery' },
                                { to: '/about', label: 'About Us' },
                                { to: '/contact', label: 'Contact' },
                            ].map(link => (
                                <li key={link.to}>
                                    <Link
                                        to={link.to}
                                        className="text-gray-400 hover:text-gold-400 transition-colors duration-300 font-sans text-sm flex items-center gap-2 group"
                                    >
                                        <span className="w-1.5 h-1.5 rounded-full bg-gold-500/30 group-hover:bg-gold-500 transition-colors duration-300" />
                                        {link.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Contact Info */}
                    <div>
                        <h4 className="font-display text-lg font-semibold mb-6 text-gold-400">Get in Touch</h4>
                        <ul className="space-y-4">
                            <li className="flex items-start gap-3">
                                <svg className="w-5 h-5 text-gold-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
                                </svg>
                                <span className="text-gray-400 font-sans text-sm">
                                    Lake road, Kookal<br />Kodaikanal, Tamil Nadu – 624103
                                </span>
                            </li>
                            <li className="flex items-center gap-3">
                                <svg className="w-5 h-5 text-gold-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" />
                                </svg>
                                <a href="tel:+91 6374422556" className="text-gray-400 hover:text-gold-400 transition-colors font-sans text-sm">
                                    +91 6374422556
                                </a>
                            </li>
                            <li className="flex items-center gap-3">
                                <svg className="w-5 h-5 text-gold-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
                                </svg>
                                <a href="mailto:ucedenvalley@gmail.com" className="text-gray-400 hover:text-gold-400 transition-colors font-sans text-sm">
                                    ucedenvalley@gmail.com
                                </a>
                            </li>
                        </ul>
                    </div>

                    {/* Experience */}
                    <div>
                        <h4 className="font-display text-lg font-semibold mb-6 text-gold-400">Experience</h4>
                        <ul className="space-y-3">
                            {['Valley Views', 'Kookal Lake', 'Campfire Nights', 'Homely Food', 'Nature Walks', 'Bird Watching'].map(item => (
                                <li key={item} className="flex items-center gap-2 text-gray-400 font-sans text-sm">
                                    <span className="w-1.5 h-1.5 rounded-full bg-gold-500/30" />
                                    {item}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>

                {/* Bottom bar */}
                <div className="border-t border-white/10 mt-12 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
                    <p className="text-gray-500 font-sans text-xs text-center md:text-left">
                        © {currentYear} UC Eden Valley. All rights reserved.
                    </p>
                    <p className="text-gray-600 font-sans text-xs">
                        Crafted with ♥ in Kodaikanal
                    </p>
                </div>
            </div>
        </footer>
    );
}
